import React, { useState, useEffect } from 'react';
import axios from 'axios';

const RecordPaymentModal = ({ show, onClose, ambassador, onPaymentRecorded }) => {
  const [paymentId, setPaymentId] = useState('');
  const [amount, setAmount] = useState('');
  const [paidAt, setPaidAt] = useState(new Date().toISOString().split('T')[0]);
  const [screenshot, setScreenshot] = useState(null);
  const [upiId, setUpiId] = useState(null);
  const [upiLoading, setUpiLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [justOpened, setJustOpened] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';

  // Fetch UPI ID when modal opens
  useEffect(() => {
    const fetchUpiId = async () => {
      if (show && ambassador?.id) {
        setUpiLoading(true);
        try {
          const token = localStorage.getItem('accessToken');
          const response = await axios.get(
            `${API_BASE_URL}/admin/campus/ambassador-overview/${ambassador.id}/upi`,
            {
              headers: { 'Authorization': `Bearer ${token}` }
            }
          );
          setUpiId(response.data.upiId);
        } catch (err) {
          console.error('Error fetching UPI ID:', err);
          setUpiId(null);
        } finally {
          setUpiLoading(false);
        }
      }
    };

    fetchUpiId();
  }, [show, ambassador, API_BASE_URL]);

  // Logging for debugging
  useEffect(() => {
    console.log('[RecordPaymentModal] Component rendered/mounted', {
      show,
      ambassador: ambassador ? { id: ambassador.id, name: ambassador.name } : null,
      timestamp: new Date().toISOString()
    });
    
    if (show) {
      // Set flag to prevent immediate backdrop clicks
      setJustOpened(true);
      const timer = setTimeout(() => {
        setJustOpened(false);
      }, 100); // Allow clicks after 100ms
      return () => clearTimeout(timer);
    } else {
      setJustOpened(false);
    }
  }, [show, ambassador]);

  if (!show || !ambassador) {
    console.log('[RecordPaymentModal] Not rendering - show:', show, 'ambassador:', ambassador);
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('[RecordPaymentModal] handleSubmit called', {
      paymentId,
      amount,
      paidAt,
      ambassadorId: ambassador?.id,
      hasScreenshot: !!screenshot
    });
    
    setError(null);
    setLoading(true);

    try {
      const token = localStorage.getItem('accessToken');
      
      // Use FormData to support file upload
      const formData = new FormData();
      formData.append('ambassadorId', ambassador.id);
      if (paymentId.trim()) {
        formData.append('paymentId', paymentId.trim());
      }
      formData.append('amount', parseFloat(amount).toString());
      formData.append('paidAt', paidAt ? new Date(paidAt).toISOString() : new Date().toISOString());
      
      // Append screenshot if selected
      if (screenshot) {
        formData.append('screenshot', screenshot);
      }

      console.log('[RecordPaymentModal] Submitting payment with FormData');

      const response = await axios.post(
        `${API_BASE_URL}/admin/campus/ambassador-overview/payment`,
        formData,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      console.log('[RecordPaymentModal] Payment recorded successfully', response.data);

      if (response.data.success) {
        onPaymentRecorded(response.data.payment);
        handleClose();
      }
    } catch (err) {
      console.error('[RecordPaymentModal] Error recording payment:', err);
      console.error('[RecordPaymentModal] Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        statusText: err.response?.statusText
      });
      setError(err.response?.data?.error || 'Failed to record payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (source = 'unknown') => {
    console.log('[RecordPaymentModal] handleClose called from:', source);
    setPaymentId('');
    setAmount('');
    setPaidAt(new Date().toISOString().split('T')[0]);
    setScreenshot(null);
    setUpiId(null);
    setError(null);
    onClose();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      setScreenshot(file);
      setError(null);
    }
  };

  const handleBackdropClick = (e) => {
    console.log('[RecordPaymentModal] Backdrop clicked', {
      target: e.target,
      currentTarget: e.currentTarget,
      isSame: e.target === e.currentTarget,
      targetClass: e.target.className,
      targetTag: e.target.tagName,
      justOpened
    });
    
    // Prevent closing if modal just opened (prevents accidental immediate closes)
    if (justOpened) {
      console.log('[RecordPaymentModal] Ignoring backdrop click - modal just opened');
      return;
    }
    
    // Only close if clicking directly on the backdrop div (modal container), not on modal content
    if (e.target === e.currentTarget && e.target.classList.contains('modal')) {
      handleClose('backdrop');
    }
  };

  const handleModalContentClick = (e) => {
    // Prevent clicks inside modal from closing it
    e.stopPropagation();
    console.log('[RecordPaymentModal] Modal content clicked', {
      target: e.target,
      targetClass: e.target.className
    });
  };

  return (
    <>
      {show && (
        <div 
          className="modal-backdrop fade show" 
          style={{ zIndex: 1040 }}
        ></div>
      )}
      <div
        className={`modal fade ${show ? 'show' : ''}`}
        style={{ display: show ? 'block' : 'none', zIndex: 1050 }}
        tabIndex="-1"
        role="dialog"
        onClick={handleBackdropClick}
      >
        <div className="modal-dialog modal-lg" role="document" onClick={handleModalContentClick} style={{ maxWidth: '900px', margin: '1.75rem auto' }}>
          <div className="modal-content" onClick={handleModalContentClick}>
          <div className="modal-header">
            <h5 className="modal-title">Record Payment</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => handleClose('close-button')}
              aria-label="Close"
            ></button>
          </div>
          <form onSubmit={handleSubmit} onClick={handleModalContentClick}>
            <div className="modal-body" onClick={handleModalContentClick}>
              <div className="mb-3">
                <label className="form-label">Ambassador</label>
                <input
                  type="text"
                  className="form-control"
                  value={ambassador.name}
                  disabled
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('[RecordPaymentModal] Ambassador input clicked');
                  }}
                />
              </div>

              {/* UPI ID Display */}
              <div className="mb-3">
                <label className="form-label">UPI ID</label>
                {upiLoading ? (
                  <div className="form-control d-flex align-items-center">
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Loading UPI ID...
                  </div>
                ) : upiId ? (
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={upiId}
                      disabled
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(upiId);
                      }}
                    >
                      <i className="bi bi-clipboard"></i> Copy
                    </button>
                  </div>
                ) : (
                  <div className="alert alert-warning mb-0 py-2 px-3 small">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Ambassador has not added UPI ID yet
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Payment ID <span className="text-muted">(Optional)</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Transaction ID, Cheque Number, etc."
                  value={paymentId}
                  onChange={(e) => {
                    e.stopPropagation();
                    console.log('[RecordPaymentModal] Payment ID changed:', e.target.value);
                    setPaymentId(e.target.value);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('[RecordPaymentModal] Payment ID input clicked');
                  }}
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Amount (₹) <span className="text-danger">*</span></label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="0.00"
                  step="0.01"
                  min="0.01"
                  required
                  value={amount}
                  onChange={(e) => {
                    e.stopPropagation();
                    console.log('[RecordPaymentModal] Amount changed:', e.target.value);
                    setAmount(e.target.value);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('[RecordPaymentModal] Amount input clicked');
                  }}
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Payment Date <span className="text-danger">*</span></label>
                <input
                  type="date"
                  className="form-control"
                  required
                  value={paidAt}
                  onChange={(e) => {
                    e.stopPropagation();
                    console.log('[RecordPaymentModal] Payment date changed:', e.target.value);
                    setPaidAt(e.target.value);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('[RecordPaymentModal] Payment date input clicked');
                  }}
                />
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Payment Screenshot <span className="text-muted">(Optional)</span>
                </label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={handleFileChange}
                  onClick={(e) => e.stopPropagation()}
                />
                {screenshot && (
                  <small className="text-muted d-block mt-2">
                    <i className="bi bi-file-image me-1"></i>
                    {screenshot.name} ({(screenshot.size / 1024).toFixed(2)} KB)
                  </small>
                )}
                <small className="text-muted d-block mt-1">
                  Upload proof of payment (Max 5MB, images only)
                </small>
              </div>

              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}
            </div>
            <div className="modal-footer" onClick={handleModalContentClick}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose('cancel-button');
                }}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('[RecordPaymentModal] Submit button clicked');
                }}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Recording...
                  </>
                ) : (
                  'Record Payment'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    </>
  );
};

export default RecordPaymentModal;

